import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";
import { CategoryChip } from "@/components/GSTR2TaxCodes";

const COLS = ["invoice_no", "accounting_document", "fiscal_year", "doc_type", "invoice_type", "tax_code", "tax_label", "tax_category", "tax_flag", "vendor_code", "vendor_name", "gstin", "place_of_supply",
  "invoice_date", "posting_date", "gst_rate", "taxable_value", "igst", "cgst", "sgst", "total_tax", "invoice_value", "itc_eligible", "gl_accounts", "hsn", "source"];

const toCsv = (lines) => {
  const esc = (v) => { const s = v == null ? "" : Array.isArray(v) ? v.join(" ") : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [COLS.join(","), ...lines.map((l) => COLS.map((c) => esc(l[c])).join(","))].join("\n");
};

export const ItcBadge = ({ v }) => (
  <Badge className={v === "Yes" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : v === "RCM" ? "bg-sky-100 text-sky-700 hover:bg-sky-100" : "bg-red-100 text-red-700 hover:bg-red-100"}>{v}</Badge>
);

/** Side panel listing the register lines behind a KPI tile, GL box, type chip, rate row, vendor or month. Click a line → document detail. */
export function GSTR2Drill({ drill, params, onClose, onOpenDoc }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!drill) return undefined;
    const ctrl = new AbortController();
    setD(null); setErr("");
    axios.get(`${API}/reports/gstr2/lines`, { params: { ...params, metric: drill.metric, value: drill.value }, signal: ctrl.signal })
      .then((r) => setD(r.data)).catch((e) => { if (!axios.isCancel(e)) setErr(e.response?.data?.detail || "Could not load lines"); });
    return () => ctrl.abort();
  }, [drill, params]);

  const download = () => {
    if (!d) return;
    const blob = new Blob([toCsv(d.lines)], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `gstr2_${drill.metric}${drill.value ? `_${String(drill.value).replace(/[^\w-]+/g, "_")}` : ""}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const t = d?.totals;
  return (
    <Sheet open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[96vw] xl:max-w-[1400px] overflow-y-auto p-0" data-testid="gstr2-drill">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <SheetTitle className="text-lg font-bold text-slate-900" data-testid="gstr2-drill-title">{d?.title || drill?.title || "Register lines"}</SheetTitle>
              <SheetDescription className="text-xs text-slate-500">
                {d ? `${d.count.toLocaleString("en-IN")} line${d.count === 1 ? "" : "s"}${d.truncated ? " (first 2,000 shown — narrow the filters or download)" : ""}` : "Loading…"}
                {params?.from_date || params?.to_date ? ` · ${params.from_date || "…"} → ${params.to_date || "…"}` : " · all dates"}{params?.vendor ? ` · vendor filter "${params.vendor}"` : ""} · click a line for the SAP document
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={download} disabled={!d} data-testid="gstr2-drill-csv" className="border-slate-300"><Download className="h-3.5 w-3.5 mr-1.5" /> CSV</Button>
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="gstr2-drill-close"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          {t && (
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 mt-3" data-testid="gstr2-drill-totals">
              {[["Lines", t.count.toLocaleString("en-IN")], ["Taxable", inrShort(t.taxable_value)], ["IGST", inrShort(t.igst)], ["CGST", inrShort(t.cgst)], ["SGST", inrShort(t.sgst)],
                ["Total tax", inrShort(t.total_tax)], ["Eligible", inrShort(t.eligible_tax)], ["Ineligible", inrShort(t.ineligible_tax)], ["RCM", inrShort(t.rcm_tax)]].map(([l, v]) => (
                <div key={l} className="rounded border border-slate-200 bg-slate-50 px-3 py-1.5"><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p><p className="text-sm font-black tabular-nums text-slate-900">{v}</p></div>
              ))}
            </div>
          )}
        </SheetHeader>
        <div className="p-4">
          {err && <p className="text-sm text-red-600" data-testid="gstr2-drill-error">{err}</p>}
          {!d && !err && <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading lines…</div>}
          {d && (
            <Table data-testid="gstr2-drill-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Invoice", "SAP Doc", "Type", "Vendor", "GSTIN", "HSN", "Inv. date", "Posting", "Rate", "Taxable", "IGST", "CGST", "SGST", "Total tax", "ITC"].map((h) => (
                    <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.lines.map((r) => (
                  <TableRow key={r.id} className="border-b border-slate-100 hover:bg-sky-50/60 cursor-pointer" onClick={() => onOpenDoc(r)} data-testid={`gstr2-drill-row-${r.id}`}>
                    <TableCell className="text-xs font-medium text-slate-800 whitespace-nowrap">{r.invoice_no}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-600 whitespace-nowrap">{r.accounting_document || "—"}{r.fiscal_year ? <span className="text-slate-400">/{r.fiscal_year}</span> : null}</TableCell>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap">{r.invoice_type}{r.tax_code ? <span className="ml-1 text-[10px] text-slate-400">{r.tax_code}</span> : null}{r.tax_label ? <span className="block text-[10px] text-slate-500">{r.tax_label} <CategoryChip cat={r.tax_category} flag={r.tax_flag} /></span> : null}</TableCell>
                    <TableCell className="text-xs text-slate-700 whitespace-nowrap max-w-[220px] truncate" title={r.vendor_name}>{r.vendor_name}<span className="ml-1 text-[10px] font-mono text-slate-400">{r.vendor_code}</span></TableCell>
                    <TableCell className="text-[11px] font-mono text-slate-500">{r.gstin}</TableCell>
                    <TableCell className="text-[11px] font-mono text-slate-600 whitespace-nowrap max-w-[160px] truncate" title={(r.hsn_lines || []).map((x) => `${x.hsn || "no HSN"} · ${x.desc || x.material} · ${inr(x.taxable)}`).join("\n")} data-testid={`gstr2-drill-hsn-${r.id}`}>{r.hsn || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap">{r.invoice_date}</TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{r.posting_date}</TableCell>
                    <TableCell className="text-xs text-slate-600">{r.gst_rate}%</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{inr(r.taxable_value)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{r.igst ? inr(r.igst) : "—"}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{r.cgst ? inr(r.cgst) : "—"}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{r.sgst ? inr(r.sgst) : "—"}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums font-semibold">{inr(r.total_tax)}</TableCell>
                    <TableCell><ItcBadge v={r.itc_eligible} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
